import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ScrollableDialog from '../common/ScrollableDialog';
import PrimaryActionButton from '../common/PrimaryActionButton';
import SalovarePhoneInput from '../common/SalovarePhoneInput';
import { fieldLabelSx, inputWrapperSx } from '../common/formFieldStyles';
import StoreCustomerService from '../../services/storeCustomerService';
import { DEFAULT_PHONE_COUNTRY_CODE, toPhoneDto } from '../../utils/phoneUtils';
import { BRAND, brandAlpha } from '../../theme/brandTokens';

export const EMAIL_CHECK_STATUS = {
  IDLE: 'idle',
  CHECKING: 'checking',
  AVAILABLE: 'available',
  EXISTING: 'existing',
  ALREADY_IN_STORE: 'alreadyInStore',
  ERROR: 'error',
};

const isValidEmail = (email) => /\S+@\S+\.\S+/.test(email.trim());

const inputStyle = (hasError, isDisabled) => ({
  width: '100%',
  padding: '12px 16px',
  border: `2px solid ${hasError ? '#f44336' : BRAND.line}`,
  borderRadius: '8px',
  fontSize: '16px',
  fontFamily: 'inherit',
  outline: 'none',
  transition: 'border-color 0.3s ease',
  backgroundColor: isDisabled ? '#f5f5f5' : '#fff',
  cursor: isDisabled ? 'not-allowed' : 'text',
});

const applyCheckResult = (result, setStatus, setExistingCustomerName, setName) => {
  if (!result.exists) {
    setStatus(EMAIL_CHECK_STATUS.AVAILABLE);
    setExistingCustomerName('');
    return;
  }

  if (result.alreadyAssignedToStore) {
    setStatus(EMAIL_CHECK_STATUS.ALREADY_IN_STORE);
    setExistingCustomerName(result.name || '');
    return;
  }

  setStatus(EMAIL_CHECK_STATUS.EXISTING);
  setExistingCustomerName(result.name || '');
  setName(result.name || '');
};

const CreateStoreCustomerDialog = ({
  open,
  onClose,
  storeId,
  onCreated,
}) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phoneCountryCode, setPhoneCountryCode] = useState(DEFAULT_PHONE_COUNTRY_CODE);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [errors, setErrors] = useState({});
  const [emailCheckStatus, setEmailCheckStatus] = useState(EMAIL_CHECK_STATUS.IDLE);
  const [phoneCheckStatus, setPhoneCheckStatus] = useState(EMAIL_CHECK_STATUS.IDLE);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [checkingPhone, setCheckingPhone] = useState(false);
  const [existingCustomerName, setExistingCustomerName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const trimmedEmail = email.trim();
  const usingEmail = Boolean(trimmedEmail);
  const phoneDto = toPhoneDto(phoneCountryCode, phoneNumber);
  const activeCheckStatus = usingEmail ? emailCheckStatus : phoneCheckStatus;
  const isPhoneOnlyPath = !usingEmail;

  const resetForm = () => {
    setEmail('');
    setName('');
    setPhoneCountryCode(DEFAULT_PHONE_COUNTRY_CODE);
    setPhoneNumber('');
    setErrors({});
    setEmailCheckStatus(EMAIL_CHECK_STATUS.IDLE);
    setPhoneCheckStatus(EMAIL_CHECK_STATUS.IDLE);
    setCheckingEmail(false);
    setCheckingPhone(false);
    setExistingCustomerName('');
    setSubmitting(false);
    setSubmitError(null);
  };

  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  const handleEmailChange = (event) => {
    setEmail(event.target.value);
    setEmailCheckStatus(EMAIL_CHECK_STATUS.IDLE);
    setExistingCustomerName('');
    setErrors((current) => ({ ...current, email: null, emailCheck: null, contact: null }));
    setSubmitError(null);
  };

  const handlePhoneNumberChange = (value) => {
    setPhoneNumber(value);
    setPhoneCheckStatus(EMAIL_CHECK_STATUS.IDLE);
    setExistingCustomerName('');
    setErrors((current) => ({ ...current, phone: null, phoneCheck: null, contact: null }));
    setSubmitError(null);
  };

  const handlePhoneCountryCodeChange = (value) => {
    setPhoneCountryCode(value);
    setPhoneCheckStatus(EMAIL_CHECK_STATUS.IDLE);
    setExistingCustomerName('');
    setErrors((current) => ({ ...current, phone: null, phoneCheck: null, contact: null }));
    setSubmitError(null);
  };

  const handleCheckEmail = async () => {
    if (!trimmedEmail) {
      setErrors((current) => ({ ...current, email: 'customersManagement.create.validation.emailRequired' }));
      return;
    }

    if (!isValidEmail(trimmedEmail)) {
      setErrors((current) => ({ ...current, email: 'customersManagement.create.validation.emailInvalid' }));
      return;
    }

    try {
      setCheckingEmail(true);
      setEmailCheckStatus(EMAIL_CHECK_STATUS.CHECKING);
      setErrors((current) => ({ ...current, email: null, emailCheck: null, contact: null }));
      const result = await StoreCustomerService.checkCustomerEmail(trimmedEmail, storeId);
      applyCheckResult(result, setEmailCheckStatus, setExistingCustomerName, setName);
    } catch (checkError) {
      console.error('Failed to check customer email:', checkError);
      setEmailCheckStatus(EMAIL_CHECK_STATUS.ERROR);
      setErrors((current) => ({ ...current, emailCheck: 'customersManagement.create.emailCheck.error' }));
    } finally {
      setCheckingEmail(false);
    }
  };

  const handleCheckPhone = async () => {
    if (!phoneDto) {
      setErrors((current) => ({ ...current, phone: 'customersManagement.create.validation.phoneRequired' }));
      return;
    }

    try {
      setCheckingPhone(true);
      setPhoneCheckStatus(EMAIL_CHECK_STATUS.CHECKING);
      setErrors((current) => ({ ...current, phone: null, phoneCheck: null, contact: null }));
      await StoreCustomerService.checkCustomerPhone(phoneDto, storeId);
      applyCheckResult({ exists: false }, setPhoneCheckStatus, setExistingCustomerName, setName);
    } catch (checkError) {
      console.error('Failed to check customer phone:', checkError);
      setPhoneCheckStatus(EMAIL_CHECK_STATUS.ERROR);
      setErrors((current) => ({ ...current, phoneCheck: 'customersManagement.create.phoneCheck.error' }));
    } finally {
      setCheckingPhone(false);
    }
  };

  const canSubmitNewCustomer = activeCheckStatus === EMAIL_CHECK_STATUS.AVAILABLE
    && name.trim().length > 0;

  const canSubmitExistingCustomer = activeCheckStatus === EMAIL_CHECK_STATUS.EXISTING;

  const handleSubmit = async () => {
    if (canSubmitExistingCustomer) {
      await createCustomer();
      return;
    }

    if (!usingEmail && !phoneDto) {
      setErrors((current) => ({ ...current, contact: 'customersManagement.create.validation.contactRequired' }));
      return;
    }

    if (!canSubmitNewCustomer) {
      if (usingEmail && emailCheckStatus !== EMAIL_CHECK_STATUS.AVAILABLE) {
        setErrors((current) => ({ ...current, emailCheck: 'customersManagement.create.validation.emailCheckRequired' }));
      }
      if (!usingEmail && phoneCheckStatus !== EMAIL_CHECK_STATUS.AVAILABLE) {
        setErrors((current) => ({ ...current, phoneCheck: 'customersManagement.create.phoneCheck.required' }));
      }
      if (!name.trim()) {
        setErrors((current) => ({ ...current, name: 'customersManagement.create.validation.nameRequired' }));
      }
      return;
    }

    await createCustomer();
  };

  const createCustomer = async () => {
    try {
      setSubmitting(true);
      setSubmitError(null);
      const payload = {
        email: usingEmail ? trimmedEmail : null,
        name: name.trim(),
      };
      if (activeCheckStatus === EMAIL_CHECK_STATUS.AVAILABLE || !usingEmail) {
        payload.phone = phoneDto;
      }
      const created = await StoreCustomerService.createStoreCustomer(payload, storeId);
      onCreated?.(created, {
        assigned: activeCheckStatus === EMAIL_CHECK_STATUS.EXISTING,
        hasEmail: usingEmail,
      });
      onClose();
    } catch (createError) {
      console.error('Failed to create store customer:', createError);
      const errorCode = createError?.response?.data?.code;
      if (errorCode === 'CustomerAlreadyInStore') {
        if (usingEmail) {
          setEmailCheckStatus(EMAIL_CHECK_STATUS.ALREADY_IN_STORE);
          setSubmitError(t('customersManagement.create.emailCheck.alreadyInStore'));
        } else {
          setPhoneCheckStatus(EMAIL_CHECK_STATUS.ALREADY_IN_STORE);
          setSubmitError(t('customersManagement.create.phoneCheck.alreadyInStore'));
        }
      } else {
        setSubmitError(createError?.response?.data?.message || t('customersManagement.create.error.creation'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const renderCheckAlert = (status, availableKey, existingKey, alreadyInStoreKey, errorKey) => {
    if (status === EMAIL_CHECK_STATUS.AVAILABLE) {
      return (
        <Alert severity="success" sx={{ mt: 2 }}>
          {t(availableKey)}
        </Alert>
      );
    }

    if (status === EMAIL_CHECK_STATUS.EXISTING) {
      return (
        <Alert severity="info" sx={{ mt: 2 }}>
          {t(existingKey, { name: existingCustomerName })}
        </Alert>
      );
    }

    if (status === EMAIL_CHECK_STATUS.ALREADY_IN_STORE) {
      return (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {t(alreadyInStoreKey)}
        </Alert>
      );
    }

    if (status === EMAIL_CHECK_STATUS.ERROR && errorKey) {
      return (
        <Alert severity="error" sx={{ mt: 2 }}>
          {t(errorKey)}
        </Alert>
      );
    }

    return null;
  };

  const nameEnabled = isPhoneOnlyPath
    ? phoneCheckStatus !== EMAIL_CHECK_STATUS.EXISTING
      && phoneCheckStatus !== EMAIL_CHECK_STATUS.ALREADY_IN_STORE
    : emailCheckStatus === EMAIL_CHECK_STATUS.AVAILABLE;
  const showNameField = nameEnabled;
  const isBusy = checkingEmail || checkingPhone || submitting;
  const submitDisabled = isBusy
    || activeCheckStatus === EMAIL_CHECK_STATUS.ALREADY_IN_STORE
    || activeCheckStatus === EMAIL_CHECK_STATUS.IDLE
    || activeCheckStatus === EMAIL_CHECK_STATUS.CHECKING
    || activeCheckStatus === EMAIL_CHECK_STATUS.ERROR;

  const checkButtonSx = {
    minWidth: 100,
    height: 48,
    borderColor: BRAND.accent,
    color: BRAND.accentText,
    '&:hover': { borderColor: BRAND.accentDark, backgroundColor: brandAlpha('accent', 0.04) },
  };

  return (
    <ScrollableDialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        title={t('customersManagement.create.title')}
        subtitle={t('customersManagement.create.subtitle')}
        dividers
        showCloseButton
        titleSx={{ pb: 1, flexShrink: 0 }}
        actionsSx={{
          flexShrink: 0,
          justifyContent: 'space-between',
        }}
        actions={(
          <>
            <Button onClick={onClose} color="inherit" disabled={isBusy}>
              {t('common.cancel')}
            </Button>
            <PrimaryActionButton
              onClick={handleSubmit}
              disabled={submitDisabled}
            >
              {canSubmitExistingCustomer
                ? t('customersManagement.create.assign')
                : t('customersManagement.create.action')}
            </PrimaryActionButton>
          </>
        )}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {submitError ? (
            <Alert severity="error" onClose={() => setSubmitError(null)}>
              {submitError}
            </Alert>
          ) : null}

          {errors.contact ? (
            <Alert severity="error">
              {t(errors.contact)}
            </Alert>
          ) : null}

          <Box>
            <Typography variant="subtitle2" sx={fieldLabelSx}>
              {t('customersManagement.create.email')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1, ...inputWrapperSx }}>
                <input
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  placeholder={t('customersManagement.create.emailPlaceholder')}
                  style={inputStyle(!!errors.email, isBusy)}
                  disabled={isBusy}
                />
              </Box>
              <Button
                variant="outlined"
                onClick={handleCheckEmail}
                disabled={isBusy || !trimmedEmail}
                sx={checkButtonSx}
              >
                {checkingEmail ? <CircularProgress size={20} sx={{ color: BRAND.accentText }} /> : t('customersManagement.create.emailCheck.check')}
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {t('customersManagement.create.emailOptional')}
            </Typography>
            {errors.email ? (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                {t(errors.email)}
              </Typography>
            ) : null}
            {errors.emailCheck && emailCheckStatus !== EMAIL_CHECK_STATUS.ERROR ? (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                {t(errors.emailCheck)}
              </Typography>
            ) : null}
            {usingEmail
              ? renderCheckAlert(
                emailCheckStatus,
                'customersManagement.create.emailCheck.available',
                'customersManagement.create.emailCheck.existingConfirmed',
                'customersManagement.create.emailCheck.alreadyInStore',
                errors.emailCheck,
              )
              : null}
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={fieldLabelSx}>
              {t('customersManagement.create.phone')}{isPhoneOnlyPath ? ' *' : ''}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <SalovarePhoneInput
                  countryCode={phoneCountryCode}
                  number={phoneNumber}
                  onCountryCodeChange={handlePhoneCountryCodeChange}
                  onNumberChange={handlePhoneNumberChange}
                  placeholder={t('customersManagement.create.phonePlaceholder')}
                  disabled={isBusy || activeCheckStatus === EMAIL_CHECK_STATUS.ALREADY_IN_STORE}
                  error={!!errors.phone}
                  autoCompleteSection="create-customer"
                />
              </Box>
              {isPhoneOnlyPath ? (
                <Button
                  variant="outlined"
                  onClick={handleCheckPhone}
                  disabled={isBusy || !phoneDto}
                  sx={checkButtonSx}
                >
                  {checkingPhone ? <CircularProgress size={20} sx={{ color: BRAND.accentText }} /> : t('customersManagement.create.emailCheck.check')}
                </Button>
              ) : null}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {t('customersManagement.create.phoneOptional')}
            </Typography>
            {errors.phone ? (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                {t(errors.phone)}
              </Typography>
            ) : null}
            {errors.phoneCheck && phoneCheckStatus !== EMAIL_CHECK_STATUS.ERROR ? (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                {t(errors.phoneCheck)}
              </Typography>
            ) : null}
            {isPhoneOnlyPath
              ? renderCheckAlert(
                phoneCheckStatus,
                'customersManagement.create.phoneCheck.available',
                'customersManagement.create.phoneCheck.existingConfirmed',
                'customersManagement.create.phoneCheck.alreadyInStore',
                errors.phoneCheck,
              )
              : null}
          </Box>

          {showNameField ? (
            <Box>
              <Typography variant="subtitle2" sx={fieldLabelSx}>
                {t('customersManagement.create.name')} *
              </Typography>
              <Box sx={inputWrapperSx}>
                <input
                  type="text"
                  value={name}
                  onChange={(event) => {
                    setName(event.target.value);
                    setErrors((current) => ({ ...current, name: null }));
                  }}
                  placeholder={t('customersManagement.create.namePlaceholder')}
                  style={inputStyle(!!errors.name, isBusy)}
                  disabled={isBusy}
                />
              </Box>
              {errors.name ? (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                  {t(errors.name)}
                </Typography>
              ) : null}
            </Box>
          ) : null}
        </Box>
      </ScrollableDialog>
  );
};

export default CreateStoreCustomerDialog;
